"use client";

import { useSearchParams } from "next/navigation";
import React, { useEffect, useMemo, useRef } from "react";
import { CategoryDetailsV1, Category } from "@repo/apis/browse-category-v1";
import { FacetEnrichments } from "@repo/apis/browse-facet-v1";
import { BrowseSearchV2, Facet, Product } from "@repo/apis/browse-search-v2";
import {
  Filters as FiltersType,
  ProductFilteringProvider,
} from "@repo/ui/product-filtering";
import { CategoryTitle, getHeaderTitle } from "@repo/utils/get-plp-title";
import { useAnalytics } from "@/components/analytics/initialise-analytics";
import { ViewItemListProductAnalytics } from "@/components/analytics/view-item-list-product-analytics";
import {
  GridLayoutProvider,
  useGridLayout,
} from "@/components/grid-layout/grid-layout-context";
import { NoResults } from "@/components/no-results";
import { Pagination } from "@/components/pagination";
import { PRODUCT_LIMIT, STRINGS } from "@/constants";
import { determineViewItemListState } from "@/utils/analytics/determine-view-item-list-state";
import { enrichFacets } from "@/utils/facets/enrich-facets";
import { ContentSEO } from "./segments/content-seo";
import { Header } from "./segments/header";
import { Products } from "./segments/products";

export type ProductListingViewProps = {
  plpData: BrowseSearchV2;
  categoryDetails?: CategoryDetailsV1;
  country: string;
  lang: string;
  keyStoreDataversion: string;
  store: string;
  enrichments: FacetEnrichments | null;
  category?: Category | null;
  pageType: "category" | "search";
  currency: string;
  trendingProducts?: Product[];
  currentPage?: number;
};

export default function ProductListingView({
  plpData,
  enrichments,
  categoryDetails,
  category,
  country,
  currency,
  keyStoreDataversion,
  lang,
  store,
  pageType,
  trendingProducts,
  currentPage = 1,
}: ProductListingViewProps) {
  const { columns } = useGridLayout();
  const searchParams = useSearchParams();
  const params = useMemo(
    () => new URLSearchParams(searchParams?.toString()),
    [searchParams],
  );

  const { itemCount, products, searchTerm, facets } = plpData;
  const maxPage = Math.max(1, Math.ceil(itemCount / PRODUCT_LIMIT));
  const noResults = itemCount === 0;
  const { headerBody } = categoryDetails?.categoryDetails || {};
  const { setSuperProperties } = useAnalytics();

  useEffect(() => {
    if (pageType !== "search") {
      return;
    }

    if (noResults) {
      setSuperProperties({
        page_type: "failed search",
        page_id: "failed search",
      });

      return;
    }

    setSuperProperties({
      page_type: "successful search",
      page_id: "successful search",
    });
  }, [pageType, noResults, setSuperProperties]);

  const pages = useMemo(
    () =>
      Array.from({ length: maxPage }, (_, i) => {
        const pageNumber = i + 1;

        if (pageNumber === 1) {
          params?.delete("page");
        } else {
          params.set("page", pageNumber.toString());
        }

        return {
          number: pageNumber,
          key: `page-${pageNumber}-${params.get("cid") || ""}`,
          query: Object.fromEntries(params.entries()),
        };
      }),
    [maxPage, params],
  );

  const transformedFacets = useMemo(() => {
    const sortFacet: Facet = {
      id: "sort",
      name: "Sort",
      facetValues: [
        { id: "", name: "Recommended", count: 0, isSelected: false },
        {
          id: "freshness",
          name: STRINGS.productListing.latestDrops,
          count: 0,
          isSelected: false,
        },
        {
          id: "pricedesc",
          name: "Highest to Lowest Price",
          count: 0,
          isSelected: false,
        },
        {
          id: "priceasc",
          name: "Lowest to Highest Price",
          count: 0,
          isSelected: false,
        },
      ],
      displayStyle: "radio",
      facetType: "sort",
      hasSelectedValues: true,
    };

    const enrichedFacets = enrichments
      ? enrichFacets(facets, enrichments)
      : facets;

    return [sortFacet, ...enrichedFacets];
  }, [facets, enrichments]);

  const initialFilters = useMemo(() => {
    const baseFilters: FiltersType = {
      priceMin: "",
      priceMax: "",
      ...transformedFacets
        .filter(({ facetType }) => facetType !== "Range")
        .reduce(
          (acc, { id, facetType }) => ({
            ...acc,
            [id]: facetType === "sort" ? "" : [],
          }),
          {},
        ),
    };

    if (searchParams)
      for (const [key, value] of searchParams.entries()) {
        if (key in baseFilters) {
          if (Array.isArray(baseFilters[key])) {
            baseFilters[key] = value.split(",");
          } else {
            baseFilters[key] = value;
          }
        }
      }

    return baseFilters;
  }, [transformedFacets, searchParams]);

  const prevSearchTermRef = useRef<string | undefined>(undefined);
  const isFirstRender = prevSearchTermRef.current === undefined;
  const hasSearchTermChanged =
    !isFirstRender && prevSearchTermRef.current !== searchTerm;
  prevSearchTermRef.current = searchTerm;

  const { headerTitle, friendlyName, headerHtmlEncoded } =
    categoryDetails?.categoryDetails || {};

  const categoryTitle = getHeaderTitle({
    headerTitle,
    friendlyName,
    headerHtmlEncoded,
  } as CategoryTitle);

  return (
    <>
      {noResults ? (
        <NoResults
          searchTerm={searchTerm}
          country={country}
          currency={currency}
          store={store}
          products={trendingProducts}
        />
      ) : (
        <>
          <ProductFilteringProvider initialFilters={initialFilters}>
            <GridLayoutProvider defaultColumns={2}>
              <Header
                categoryTitle={categoryTitle}
                searchQuery={searchTerm}
                itemCount={itemCount}
                facets={transformedFacets}
                category={category}
              />
              <Products
                searchQuery={searchTerm}
                categoryDetails={categoryDetails}
                products={products || []}
                country={country}
                lang={lang}
                keyStoreDataversion={keyStoreDataversion}
                store={store}
                currency={currency}
                facets={transformedFacets}
                itemCount={itemCount}
              />
              {maxPage > 1 ? (
                <Pagination
                  pages={pages}
                  searchParams={params}
                  currentPage={currentPage}
                />
              ) : null}
            </GridLayoutProvider>
          </ProductFilteringProvider>
          {headerBody ? (
            <ContentSEO title={categoryTitle} body={headerBody} />
          ) : null}
        </>
      )}
      <ViewItemListProductAnalytics
        products={products}
        item_list_name={pageType}
        search_term={searchTerm || undefined}
        searchType={searchTerm ? "free text" : undefined}
        categoryID={categoryDetails?.categoryDetails.id}
        pagination={
          maxPage > 1
            ? {
                totalProducts: itemCount,
                currentPage,
                columns,
                itemsPerPage: PRODUCT_LIMIT,
              }
            : undefined
        }
        facets={transformedFacets}
        filters={initialFilters}
        item_list_state={determineViewItemListState({
          pageType,
          searchTerm:
            isFirstRender || hasSearchTermChanged ? searchTerm : undefined,
          itemCount,
          currentPage,
          facets: transformedFacets,
          filters: initialFilters,
        })}
      />
    </>
  );
}
